// The page's handle on the engine. Features use this, never the adapters.

import type { Capabilities, ConvertJob, JobResult, MediaInfo, Progress } from '../types';
import type { FromWorker, ToWorker } from './protocol';
import { EngineError } from '../errors';

type Pending = {
  resolve: (value: never) => void;
  reject: (err: EngineError) => void;
  onProgress?: (p: Progress) => void;
};

export class EngineClient {
  private worker = new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' });
  private nextId = 1;
  private pending = new Map<number, Pending>();

  constructor() {
    this.worker.onmessage = ({ data }: MessageEvent<FromWorker>) => {
      const p = this.pending.get(data.id);
      if (!p) return;
      if (data.type === 'progress') {
        p.onProgress?.(data.progress);
        return;
      }
      this.pending.delete(data.id);
      if (data.type === 'error') p.reject(new EngineError(data.code, data.message));
      else if (data.type === 'capabilities') p.resolve(data.data as never);
      else if (data.type === 'probe') p.resolve(data.info as never);
      else if (data.type === 'done') p.resolve(data.result as never);
    };
    this.worker.onerror = (event) => {
      const err = new EngineError('unknown', `The converter stopped unexpectedly${event.message ? `: ${event.message}` : '.'}`);
      for (const p of this.pending.values()) p.reject(err);
      this.pending.clear();
    };
  }

  private request<T>(msg: ToWorker, onProgress?: (p: Progress) => void): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.pending.set(msg.id, { resolve: resolve as (v: never) => void, reject, onProgress });
      this.worker.postMessage(msg);
    });
  }

  capabilities(): Promise<Capabilities> {
    return this.request({ type: 'capabilities', id: this.nextId++ });
  }

  probe(file: File): Promise<MediaInfo> {
    return this.request({ type: 'probe', id: this.nextId++, file });
  }

  run(job: ConvertJob, onProgress: (p: Progress) => void): { id: number; result: Promise<JobResult> } {
    const id = this.nextId++;
    return { id, result: this.request<JobResult>({ type: 'run', id, job }, onProgress) };
  }

  cancel(id: number): void {
    this.worker.postMessage({ type: 'cancel', id } satisfies ToWorker);
  }
}
